import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, Outlet } from "react-router-dom";
import { Show, SignOutButton } from "@clerk/react";
import { Toaster } from "@/components/ui/sonner";
import ClerkAuthProvider from "@/components/auth/ClerkAuthProvider";
import { MeProvider, useMe } from "@/context/MeContext";
import AppLayout from "@/components/AppLayout";
import { Loader } from "@/components/common";
import Landing from "@/pages/Landing";
import AuthPage from "@/pages/AuthPage";
import Onboarding from "@/pages/Onboarding";
import Jobs from "@/pages/Jobs";
import JobDetail from "@/pages/JobDetail";
import JobForm from "@/pages/JobForm";
import MyJobs from "@/pages/MyJobs";
import JobApplicants from "@/pages/JobApplicants";
import MyApplications from "@/pages/MyApplications";
import People from "@/pages/People";
import UserProfile from "@/pages/UserProfile";
import Network from "@/pages/Network";
import Vip, { PaymentResult } from "@/pages/Vip";
import Admin from "@/pages/Admin";
import Community, { GroupPage } from "@/pages/Community";
import { Terms, Privacy, Contact } from "@/pages/Legal";
import Messages from "@/pages/Messages";
import Help from "@/pages/Help";
import Companies from "@/pages/Companies";
import ProfileEdit from "@/pages/ProfileEdit";

function Gate() {
  const { me, failed, refresh, blocked } = useMe();
  if (blocked) return (
    <div className="min-h-screen flex items-center justify-center p-6">
      <div className="panel p-8 max-w-md text-center" data-testid="blocked-screen">
        <h1 className="text-2xl font-extrabold">Your account has been blocked</h1>
        {blocked.reason && <p className="mt-3 text-slate-600" data-testid="blocked-reason">Reason: {blocked.reason}</p>}
        <p className="mt-3 text-sm text-slate-500">If you think this is a mistake, contact info@wg-exakt.se.</p>
        <SignOutButton><button className="mt-6 underline text-sm" data-testid="blocked-signout-button">Sign out</button></SignOutButton>
      </div>
    </div>
  );
  if (failed && !me) return (
    <div className="p-10 text-center"><p>Could not load your account.</p><button className="mt-3 underline" onClick={refresh} data-testid="retry-me-button">Try again</button></div>
  );
  if (!me) return <Loader />;
  if (!me.role) return <Onboarding />;
  return <AppLayout><Outlet /></AppLayout>;
}

const Protected = () => (
  <>
    <Show when="signed-in"><MeProvider><Gate /></MeProvider></Show>
    <Show when="signed-out"><Navigate to="/" replace /></Show>
  </>
);

const RoleOnly = ({ role }) => {
  const { me } = useMe();
  return me.role === role ? <Outlet /> : <Navigate to="/jobs" replace />;
};

const AdminOnly = () => {
  const { me } = useMe();
  return me.access_role === "admin" ? <Outlet /> : <Navigate to="/jobs" replace />;
};

const Home = () => (
  <>
    <Show when="signed-out"><Landing /></Show>
    <Show when="signed-in"><Navigate to="/community" replace /></Show>
  </>
);

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <ClerkAuthProvider>
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/sign-in/*" element={<AuthPage mode="in" />} />
            <Route path="/sign-up/*" element={<AuthPage mode="up" />} />
            <Route path="/terms" element={<Terms />} />
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/companies" element={<Companies />} />
            <Route element={<Protected />}>
              <Route path="/jobs" element={<Jobs />} />
              <Route path="/jobs/:id" element={<JobDetail />} />
              <Route element={<RoleOnly role="company" />}>
                <Route path="/jobs/new" element={<JobForm />} />
                <Route path="/jobs/:id/edit" element={<JobForm />} />
                <Route path="/my-jobs" element={<MyJobs />} />
                <Route path="/my-jobs/:id/applicants" element={<JobApplicants />} />
              </Route>
              <Route element={<RoleOnly role="technician" />}>
                <Route path="/applications" element={<MyApplications />} />
              </Route>
              <Route path="/people" element={<People />} />
              <Route path="/u/:id" element={<UserProfile />} />
              <Route path="/network" element={<Network />} />
              <Route path="/community" element={<Community />} />
              <Route path="/community/groups/:id" element={<GroupPage />} />
              <Route path="/contacts" element={<Navigate to="/network" replace />} />
              <Route path="/vip" element={<Vip />} />
              <Route path="/payment/success" element={<PaymentResult />} />
              <Route path="/payment/cancel" element={<PaymentResult />} />
              <Route element={<AdminOnly />}>
                <Route path="/admin" element={<Admin />} />
              </Route>
              <Route path="/messages" element={<Messages />} />
              <Route path="/messages/:uid" element={<Messages />} />
              <Route path="/help" element={<Help />} />
              <Route path="/profile" element={<ProfileEdit />} />
            </Route>
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </ClerkAuthProvider>
        <Toaster position="top-center" richColors />
      </BrowserRouter>
    </div>
  );
}

export default App;
